<template>
  <v-dialog v-model="show" persistent max-width="900px">
    <v-card class="mb-0">
      <v-toolbar dark color="primary">
        <v-btn icon dark @click="show = false"
          ><v-icon>mdi-close</v-icon></v-btn
        >
        <v-toolbar-title>
          Movimientos — {{ nombreProducto }}
          <span v-if="stockActual !== null" class="ml-3">
            <v-chip
              small
              :color="
                stockActual <= 0 || stockActual <= stockMinimo
                  ? 'error'
                  : stockActual <= puntoReposicion
                  ? 'warning'
                  : 'success'
              "
              dark
            >
              Stock: {{ stockActual }}
            </v-chip>
          </span>
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn text dark @click="abrirNuevoMovimiento">
          <v-icon left>mdi-plus</v-icon>Nuevo
        </v-btn>
      </v-toolbar>

      <v-card-text class="pt-4">
        <!-- Filtros de fecha -->
        <v-row dense>
          <v-col cols="12" sm="4">
            <v-text-field
              v-model="filtros.fechaDesde"
              label="Desde"
              type="date"
              dense
              prepend-icon="mdi-calendar"
              hide-details
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="4">
            <v-text-field
              v-model="filtros.fechaHasta"
              label="Hasta"
              type="date"
              dense
              prepend-icon="mdi-calendar"
              hide-details
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="4" class="d-flex align-center">
            <v-btn
              color="primary"
              small
              @click="cargarMovimientos"
              :loading="loading"
            >
              <v-icon left small>mdi-magnify</v-icon>Filtrar
            </v-btn>
          </v-col>
        </v-row>

        <!-- Tabla -->
        <v-data-table
          v-if="movimientos.length"
          :headers="headers"
          :items="movimientos"
          class="elevation-1 mt-3"
          locale="es-ar"
          item-key="idMovimiento"
          :items-per-page="15"
          :footer-props="{
            itemsPerPageText: 'Filas',
            itemsPerPageOptions: [10, 15, 30],
          }"
          :loading="loading"
          dense
        >
          <template v-slot:item.tipo="{ item }">
            <v-chip x-small :color="colorTipo(item.tipo)" dark>{{
              item.tipo
            }}</v-chip>
          </template>
          <template v-slot:item.cantidad="{ item }">
            <span
              :class="
                esEgreso(item)
                  ? 'red--text font-weight-bold'
                  : 'green--text font-weight-bold'
              "
            >
              {{ esEgreso(item) ? "-" : "+" }}{{ Math.abs(item.cantidad) }}
            </span>
          </template>
          <template v-slot:item.precioCosto="{ item }">
            <span v-if="item.precioCosto != null"
              >${{ parseFloat(item.precioCosto).toFixed(2) }}</span
            >
            <span v-else class="grey--text">—</span>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-tooltip top v-if="item.tipo !== 'Consumo'">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  small
                  color="red"
                  v-on="on"
                  v-bind="attrs"
                  @click="borrarMovimiento(item)"
                >
                  mdi-delete
                </v-icon>
              </template>
              <span>Eliminar (revierte stock)</span>
            </v-tooltip>
            <span v-else class="grey--text caption">Auto</span>
          </template>
        </v-data-table>

        <v-alert
          v-else-if="!loading"
          color="purple"
          dark
          outlined
          dense
          class="mt-3"
        >
          No hay movimientos registrados para este producto.
        </v-alert>
      </v-card-text>

      <!-- Modal nuevo movimiento -->
      <v-dialog v-model="showNuevoMovimiento" persistent max-width="480px">
        <v-card class="mb-0">
          <v-toolbar dark color="primary" dense>
            <v-btn icon dark @click="showNuevoMovimiento = false"
              ><v-icon>mdi-close</v-icon></v-btn
            >
            <v-toolbar-title>Nuevo Movimiento</v-toolbar-title>
          </v-toolbar>
          <v-card-text class="pt-4">
            <v-form
              ref="formMovimiento"
              v-model="validMovimiento"
              :lazy-validation="lazy"
            >
              <v-row dense>
                <v-col cols="12">
                  <v-select
                    v-model="nuevoMovimiento.tipo"
                    :items="tiposMovimiento"
                    label="Tipo *"
                    :rules="requiredRules"
                    dense
                    prepend-icon="mdi-swap-vertical"
                  ></v-select>
                </v-col>
              </v-row>
              <v-row dense>
                <v-col cols="12">
                  <v-text-field
                    v-model="nuevoMovimiento.cantidad"
                    :label="
                      nuevoMovimiento.tipo === 'Ajuste'
                        ? 'Cantidad (positivo suma, negativo resta) *'
                        : nuevoMovimiento.tipo === 'FijarStock'
                        ? 'Stock real *'
                        : 'Cantidad *'
                    "
                    :rules="nuevoMovimiento.tipo === 'FijarStock' ? fijarStockRules : cantidadRules"
                    :hint="nuevoMovimiento.tipo === 'FijarStock' && stockActual !== null ? `Stock actual: ${stockActual}` : ''"
                    :persistent-hint="nuevoMovimiento.tipo === 'FijarStock'"
                    dense
                    prepend-icon="mdi-counter"
                    type="number"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row dense v-if="nuevoMovimiento.tipo === 'Ingreso'">
                <v-col cols="12">
                  <v-text-field
                    v-model="nuevoMovimiento.precioCosto"
                    label="Precio de Costo (actualiza el producto)"
                    dense
                    prepend-icon="mdi-currency-usd"
                    @keypress="onlyNumber"
                    @blur="toFixedCosto"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row dense>
                <v-col cols="12">
                  <v-text-field
                    v-model="nuevoMovimiento.observaciones"
                    label="Observaciones"
                    dense
                    prepend-icon="mdi-text"
                    maxlength="200"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text color="grey" @click="showNuevoMovimiento = false"
              >Cancelar</v-btn
            >
            <v-btn
              color="success"
              :disabled="!validMovimiento"
              @click="guardarMovimiento"
              >Guardar</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from "@/axios-auth";
import { showAlert } from "@/utils/toast";

export default {
  props: {
    visible: { type: Boolean, required: true },
    idProducto: { type: Number, required: true },
    nombreProducto: { type: String, required: false, default: "" },
    stockInicial: { type: Number, required: false, default: null },
    stockMinimo: { type: Number, required: false, default: null },
    puntoReposicion: { type: Number, required: false, default: null },
  },
  data() {
    return {
      movimientos: [],
      stockActual: null,
      loading: false,
      filtros: { fechaDesde: null, fechaHasta: null },
      headers: [
        { text: "Fecha", value: "fechaString" },
        { text: "Tipo", value: "tipo" },
        { text: "Cantidad", value: "cantidad" },
        { text: "Precio Costo", value: "precioCosto" },
        { text: "Observaciones", value: "observaciones" },
        { text: "Usuario", value: "usuario" },
        { text: "", value: "actions", sortable: false },
      ],
      showNuevoMovimiento: false,
      validMovimiento: false,
      lazy: false,
      nuevoMovimiento: {
        tipo: "Ingreso",
        cantidad: null,
        precioCosto: null,
        observaciones: "",
      },
      tiposMovimiento: [
        { text: "Ingreso (compra/recepción)", value: "Ingreso" },
        { text: "Egreso (rotura/vencimiento)", value: "Egreso" },
        { text: "Ajuste de inventario", value: "Ajuste" },
        { text: "Fijar stock (ajuste automático)", value: "FijarStock" },
      ],
      requiredRules: [(v) => !!v || "Dato obligatorio"],
      cantidadRules: [
        (v) =>
          (v !== null && v !== "" && v !== undefined) || "Dato obligatorio",
        (v) => v != 0 || "La cantidad no puede ser cero",
      ],
      fijarStockRules: [
        (v) =>
          (v !== null && v !== "" && v !== undefined) || "Dato obligatorio",
        (v) => parseFloat(v) >= 0 || "El stock real no puede ser negativo",
      ],
    };
  },
  computed: {
    show: {
      get() {
        return this.visible;
      },
      set(val) {
        if (!val) {
          this.filtros = { fechaDesde: null, fechaHasta: null };
          this.movimientos = [];
          this.$emit("close");
        }
      },
    },
  },
  watch: {
    visible(val) {
      if (val && this.idProducto) this.cargarMovimientos();
    },
  },
  methods: {
    esEgreso(item) {
      return item.tipo === "Egreso" || item.tipo === "Consumo";
    },
    colorTipo(tipo) {
      const c = {
        Ingreso: "green",
        Egreso: "red",
        Ajuste: "orange",
        Consumo: "blue",
      };
      return c[tipo] || "grey";
    },
    async cargarMovimientos() {
      this.loading = true;
      try {
        const { data } = await axios.get("/MovimientoStock/GetMovimientos", {
          params: {
            idProducto: this.idProducto,
            idInstitucion: this.$store.state.usuario.institucion.idInstitucion,
            fechaDesde: this.filtros.fechaDesde || null,
            fechaHasta: this.filtros.fechaHasta || null,
          },
        });

        // Construir lista: si hay stockInicial, agregar fila sintética al final
        const lista = data || [];
        if (this.stockInicial !== null && this.stockInicial > 0) {
          lista.push({
            idMovimiento: 0,
            tipo: "Ingreso",
            cantidad: this.stockInicial,
            precioCosto: null,
            fechaString: "—",
            observaciones: "Stock inicial del producto",
            usuario: "—",
            _esStockInicial: true,
          });
        }
        this.movimientos = lista;
        await this.cargarStockActual();
      } catch {
        this.movimientos = [];
      } finally {
        this.loading = false;
      }
    },
    async cargarStockActual() {
      try {
        const { data } = await axios.get("/Producto/BuscarDatosProducto", {
          params: { idProducto: this.idProducto },
        });
        if (data) this.stockActual = data.stockActual;
      } catch {
        /* silencioso */
      }
    },
    abrirNuevoMovimiento() {
      this.nuevoMovimiento = {
        tipo: "Ingreso",
        cantidad: null,
        precioCosto: null,
        observaciones: "",
      };
      if (this.$refs.formMovimiento)
        this.$refs.formMovimiento.resetValidation();
      this.showNuevoMovimiento = true;
    },
    async guardarMovimiento() {
      // Si es "FijarStock", calcular el delta entre stock real y actual
      let tipo = this.nuevoMovimiento.tipo;
      let cantidad = parseFloat(this.nuevoMovimiento.cantidad);
      let observaciones = this.nuevoMovimiento.observaciones || null;

      if (tipo === "FijarStock") {
        if (this.stockActual === null) {
          showAlert({
            icon: "warning",
            title: "No se pudo obtener el stock actual. Intente nuevamente.",
            vm: this,
            timer: 3000,
          });
          return;
        }
        const delta = cantidad - this.stockActual;
        if (delta === 0) {
          showAlert({
            icon: "info",
            title: "El stock actual ya es igual al deseado. No se requiere ajuste.",
            vm: this,
            timer: 3000,
          });
          return;
        }
        tipo = "Ajuste";
        cantidad = delta;
        observaciones = observaciones
          ? `Ajuste a stock ${this.nuevoMovimiento.cantidad} (era ${this.stockActual}). ${observaciones}`
          : `Ajuste a stock ${this.nuevoMovimiento.cantidad} (era ${this.stockActual})`;
      }

      const dto = {
        idProducto: this.idProducto,
        idInstitucion: this.$store.state.usuario.institucion.idInstitucion,
        tipo: tipo,
        cantidad: cantidad,
        precioCosto:
          this.nuevoMovimiento.tipo === "Ingreso" &&
          this.nuevoMovimiento.precioCosto
            ? parseFloat(this.nuevoMovimiento.precioCosto)
            : null,
        observaciones: observaciones,
        usuario: this.$store.state.usuario.usuario,
      };
      try {
        await axios.post("/MovimientoStock/GuardarMovimiento", dto);
        showAlert({
          icon: "success",
          title: "Movimiento registrado.",
          vm: this,
          timer: 2000,
        });
        this.showNuevoMovimiento = false;
        await this.cargarMovimientos();
        this.$emit("stockActualizado");
      } catch (e) {
        showAlert({
          icon: "warning",
          title: e.response?.data || "No se pudo registrar el movimiento.",
          vm: this,
          timer: 3000,
        });
      }
    },
    borrarMovimiento(item) {
      if (item._esStockInicial) return;
      this.$swal({
        title: "Eliminar movimiento",
        text: "¿Desea eliminar este movimiento? El stock será revertido.",
        icon: "warning",
        background: "#ececec",
        showCancelButton: true,
        confirmButtonText: "Si",
        cancelButtonText: "No",
        showCloseButton: true,
      }).then(async (result) => {
        if (!result.value) return;
        try {
          await axios.get("/MovimientoStock/BorrarMovimiento", {
            params: { idMovimiento: item.idMovimiento },
          });
          showAlert({
            icon: "success",
            title: "Movimiento eliminado.",
            vm: this,
            timer: 2000,
          });
          await this.cargarMovimientos();
          this.$emit("stockActualizado");
        } catch (e) {
          showAlert({
            icon: "warning",
            title: e.response?.data || "No se pudo eliminar el movimiento.",
            vm: this,
            timer: 3000,
          });
        }
      });
    },
    toFixedCosto() {
      if (isNaN(parseFloat(this.nuevoMovimiento.precioCosto)))
        this.nuevoMovimiento.precioCosto = null;
      else
        this.nuevoMovimiento.precioCosto = parseFloat(
          this.nuevoMovimiento.precioCosto,
        ).toFixed(2);
    },
    onlyNumber($event) {
      const k = $event.keyCode ? $event.keyCode : $event.which;
      if ((k < 48 || k > 57) && k !== 46) $event.preventDefault();
    },
  },
};
</script>
